"use client";

import type { NewsSectionComponent } from "../../types";
import { ArrowRight } from "lucide-react";
import { NewsItemCard } from "../../components/home/cards/NewsItemCard";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

/** News section renderer with featured article and news list. */
export function NewsSectionRenderer({ component }: { component: NewsSectionComponent }) {
  const locale = useLocale();
  const featured = component.content?.featured;
  const items = Array.isArray(component.content?.items) ? component.content.items : [];
  const hasFeatured =
    !!featured &&
    Boolean(featured.title || featured.excerpt || resolveLinkHref(featured.href));
  if (!hasFeatured && items.length === 0) return null;

  const { src, options } = resolveImageValue(featured?.image);
  const featuredImage = src || "/media/welcome_background.jpg";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const featuredHref = featured ? resolveLinkHref(featured.href) ?? "/" : "/";
  return (
    <section className="relative mx-auto max-w-7xl px-6 md:px-8 py-6 md:py-10">
      <div className={mergeClassNames("grid grid-cols-1 gap-8", hasFeatured ? "md:grid-cols-2" : "md:grid-cols-1")}>
        {hasFeatured && featured && (
          <article className="overflow-hidden rounded-sm bg-white">
            <div
              className={mergeClassNames(
                "relative w-full bg-slate-200 aspect-[16/10] md:aspect-auto md:h-[400px] lg:h-[440px] max-h-[440px] overflow-hidden",
                overrideProps.className
              )}
              style={overrideProps.style}
            >
              <RuntimeImage
                src={featuredImage}
                alt={featured.title}
                className="absolute inset-0 h-full w-full object-cover object-center"
                style={objectPositionStyle}
                priority
              />
            </div>
            <div className="p-5 px-4 md:p-6 space-y-4 bg-[#E6F4EA]">
              <h3 className="text-[#00A356] font-semibold leading-tight md:leading-snug text-2xl" style={{ fontFamily: "Nunito Sans" }}>
                {featured.title}
              </h3>
              <p className="text-base xl:text-lg text-slate-600 line-clamp-4" style={{ fontFamily: "Nunito Sans" }}>
                {featured.excerpt}
              </p>
              <RuntimeLink
                className="inline-flex items-center gap-2 rounded-sm bg-[#FFC105] px-5 py-2.5 text-sm xl:text-base font-semibold uppercase tracking-wide text-white hover:bg-[#e2920e] w-fit"
                href={featuredHref}
              >
                {t(locale, "readMoreShort")}
                <ArrowRight className="h-4 w-4" />
              </RuntimeLink>
            </div>
          </article>
        )}

        <div className="grid grid-cols-1 gap-4 content-start">
          {items.map((n) => (
            <NewsItemCard key={n.id || n.title} title={n.title} excerpt={n.excerpt} image={n.image || ""} href={n.href} />
          ))}
        </div>
      </div>
    </section>
  );
}


